import { z } from 'zod';
import { label, personName } from './text-policy.js';
import { directoryBioSchema, directoryPronounsSchema } from './employee-directory.js';
import { directoryProviderSchema } from './directory-provider.js';
import { optionalE164Schema } from './phone.js';
/**
 * What kind of directory record this is (docs/004-domain-model.md).
 *
 * Must stay identical — same members, **same order** — to the `PersonType`
 * Prisma enum in packages/db/prisma/schema.prisma. docs/adr/0011 requires the
 * mirror to be proven rather than remembered; the lock-step test lives in
 * packages/db/src/directory-enums.test.ts, because this package is browser-safe
 * and may never import the Prisma client to check for itself.
 */
export const PERSON_TYPES = ['EMPLOYEE', 'CONTRACTOR'];
export const personTypeSchema = z.enum(PERSON_TYPES);
/** `?source=` on GET /people: SYNCED = has a directorySource, MANUAL = has none (S49-3u50). */
export const PERSON_SOURCE_FILTERS = ['SYNCED', 'MANUAL'];
export const personSourceFilterSchema = z.enum(PERSON_SOURCE_FILTERS);
/** Same lock-step contract as `PERSON_TYPES` above, for `EmploymentStatus`. */
export const EMPLOYMENT_STATUSES = ['ACTIVE', 'INACTIVE', 'TERMINATED'];
export const employmentStatusSchema = z.enum(EMPLOYMENT_STATUSES);
/**
 * The wire shape of a person. An allow-list, deliberately narrower than the row.
 *
 * Absent on purpose, and not an oversight:
 *   - `tenantId` — comes from the verified token, never from a body (docs/008 S1).
 *   - `externalId` — the HR system's key. A column since S3 (retrofitting one
 *     onto a populated directory is the expensive version) with no API surface
 *     until a feature needs it.
 *   - `userId` — the link to a login. Same reasoning; nothing sets it in S3, and
 *     exposing an auth principal id through a directory read is a widening
 *     nobody has asked for.
 *
 * `email` and `phone` ARE here: the directory's whole job is to hold them. They
 * are PII, so docs/008 S7 keeps them off the pino log line and out of every
 * `AuditEvent.metadata` allow-list — this DTO is the one place they travel.
 * `pronouns` and `bio` (S47-rsq5 Phase 5) are here for the same reason and on the same terms.
 *   - `directorySource` — which external directory owns this person (null = Manual), S49-3u50.
 *   - `directorySyncOptOut` — an admin turned off directory sync for this person (S49-3u50 Phase 2a).
 */
export const personDTOSchema = z.object({
    id: z.string().min(1),
    personType: personTypeSchema,
    firstName: z.string().min(1),
    // Deliberately NOT `.min(1)`: a mononym backfilled from the pre-split `name`
    // column lands entirely in `firstName` with `lastName` set to `''` (see the
    // migration in packages/db/prisma/migrations). The DTO must still parse rows
    // like that.
    lastName: z.string(),
    // Derived, not stored: `${firstName} ${lastName}`.trim()`, computed by
    // `toPersonDTO` in the API. There is no `name` column on `Person` anymore.
    name: z.string().min(1),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    title: z.string().nullable(),
    // S47-rsq5 Phase 5: the employee's own words (adr/0036 addendum "Profile fields and the import"). PII,
    // like email and phone: never in a log line or audit metadata. Plain text.
    pronouns: z.string().nullable(),
    bio: z.string().nullable(),
    departmentId: z.string().min(1).nullable(),
    managerId: z.string().min(1).nullable(),
    primaryLocationId: z.string().min(1).nullable(),
    secondaryLocationIds: z.array(z.string().min(1)),
    assistantPersonIds: z.array(z.string().min(1)),
    employmentStatus: employmentStatusSchema,
    directorySource: directoryProviderSchema.nullable(),
    directorySyncOptOut: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * The rule shared by create and update: an `EMPLOYEE` record must carry an
 * email. Exported so the API service can run the identical check on PATCH,
 * where the payload is partial and the stored email has to be merged in first
 * — `updatePersonSchema` itself cannot see the stored row, so it cannot apply
 * this rule on its own (see the comment on `updatePersonSchema` below).
 */
export const requiresEmail = (personType) => personType === 'EMPLOYEE';
/**
 * Adds the `EMAIL_REQUIRED_FOR_EMPLOYEE` issue at `path: ['email']` when
 * `requiresEmail` fires and there is no email. Shared by `createPersonSchema`
 * below and by the API service for PATCH, so the rule is defined exactly once.
 */
export function checkEmployeeEmail(value, ctx) {
    if (requiresEmail(value.personType) && !value.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'EMAIL_REQUIRED_FOR_EMPLOYEE',
            path: ['email'],
        });
    }
}
/**
 * The fields shared by create and update, before either's extra rule
 * (`EMAIL_REQUIRED_FOR_EMPLOYEE` / `EMPTY_UPDATE`) is layered on. Kept as a
 * plain `ZodObject` — not a `ZodEffects` — specifically so `.partial()` stays
 * available for `updatePersonSchema` below; `.superRefine()`/`.refine()` strip
 * that off.
 *
 * Only `personType`, `firstName` and `lastName` are required. A contractor
 * with no work email, no phone and no title is a real directory record, so
 * those three default to `null` rather than being demanded of the form.
 */
/**
 * Arbitrary, generous safety caps on how many secondary locations or assistants one
 * person may carry (S30 Phase 1). No product requirement names a limit — these exist
 * purely so a pathological payload cannot create thousands of join rows in one
 * request (docs/008 S10's spirit: bound anything a single request can multiply). Chosen
 * to be far larger than any real org chart; revisit only if a real tenant hits either.
 */
export const MAX_PERSON_SECONDARY_LOCATIONS = 50;
export const MAX_PERSON_ASSISTANTS = 50;
export const personInputSchema = z.object({
    personType: personTypeSchema,
    firstName: personName({ max: 100 }),
    lastName: personName({ max: 100 }),
    email: z.string().trim().email().max(254).nullable().default(null),
    // E.164 only — `PhoneInput` is the one control that writes this column. See
    // `phone.ts` for why the READ schema above stays an unconstrained string.
    phone: optionalE164Schema,
    title: label({ max: 120 }).nullable().default(null),
    // S47-rsq5 Phase 5 (Constraint 11): Directory-only form fields, accepted on every tenant. OPTIONAL with
    // NO default — an omitted key never clears a value the employee wrote (PATCH), and existing callers'
    // `CreatePersonInput` literals stay valid. `null` clears. Same plain-text rules as the self-edit route.
    pronouns: directoryPronounsSchema.nullable().optional(),
    bio: directoryBioSchema.nullable().optional(),
    departmentId: z.string().min(1).nullable().default(null),
    managerId: z.string().min(1).nullable().default(null),
    primaryLocationId: z.string().min(1).nullable().default(null),
    secondaryLocationIds: z.array(z.string().min(1)).max(MAX_PERSON_SECONDARY_LOCATIONS).default([]),
    assistantPersonIds: z.array(z.string().min(1)).max(MAX_PERSON_ASSISTANTS).default([]),
    employmentStatus: employmentStatusSchema.default('ACTIVE'),
});
export const createPersonSchema = personInputSchema.superRefine(checkEmployeeEmail);
/**
 * Every field optional, but at least one present.
 *
 * **`managerId: null` is a first-class operation**, not a special case: clearing
 * a reporting line is the S3 Phase 4 requirement. `.partial()` only makes the
 * key omittable — the `.nullable()` from `personInputSchema` survives it — so
 * an explicit `null` parses to `null` and an omitted key is simply absent from
 * the parsed object. The service can therefore tell "clear the manager" from
 * "do not touch the manager", which is exactly what a PATCH needs and what the
 * `EMPTY_UPDATE` refinement below relies on. `person.test.ts` pins both halves.
 *
 * Built from `personInputSchema`, NOT from `createPersonSchema`: `.partial()`
 * does not exist on a `ZodEffects`, which is what `.superRefine()` produces.
 * The `EMAIL_REQUIRED_FOR_EMPLOYEE` rule is deliberately NOT applied here — a
 * partial PATCH payload cannot see the stored email, so only the API service
 * (which merges the payload onto the stored row first) can enforce it. It
 * reuses `requiresEmail/checkEmployeeEmail` above to do so.
 */
export const updatePersonSchema = personInputSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'EMPTY_UPDATE' });
//# sourceMappingURL=person.js.map